import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { holdExpiryHours } from '@/domain/depositWindow';
import DashboardKPITile from '@/components/DashboardKPITile';
import HoldToSign from '@/components/HoldToSign';
import { aud, customerName, simTimeFull } from '@/utils/format';
import { AWAITING_CLIENT, getOrCreateUniversalLink, pinSelfBookSlot } from '@/domain/selfBookFlow';
import { settlePayouts } from '@/domain/payoutFlow';
import { artistResultsFeed } from '@/domain/reportingEngine';
import ArtistSelfBookWeek from '@/components/artist/ArtistSelfBookWeek';
import ArtistWeekSchedule from '@/components/artist/ArtistWeekSchedule';
import ArtistMonthGrid from '@/components/artist/ArtistMonthGrid';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import HoldLengthChooser from '@/components/artist/HoldLengthChooser';
import SelfBookStatusList from '@/components/artist/SelfBookStatusList';
import ArtistEarningsDay from '@/components/artist/ArtistEarningsDay';
import { buildArtistMonth } from '@/domain/artistMonth';
import type { SelfBookLink } from '@/domain/types';
import { addDays, addMonths, format, isSameDay } from 'date-fns';
import {
  Sparkles,
  Wallet,
  CheckCircle2,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

type Tab = 'today' | 'tomorrow' | 'week' | 'month' | 'self-book' | 'earnings';

export default function ArtistDashboard() {
  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const selfBookLinks = useBrainStore((s) => s.selfBookLinks);
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const artist = artistId ? artists[artistId] : undefined;
  const deposits = useBrainStore((s) => s.deposits);
  const [tab, setTab] = useState<Tab>('today');
  const [copied, setCopied] = useState(false);
  const [chooserStartIso, setChooserStartIso] = useState<string | null>(null);
  const [showRebookClients, setShowRebookClients] = useState(false);
  // Month tab: how many months off the clock we are paging, and which day's
  // breakdown is open beneath the grid.
  const [monthOffset, setMonthOffset] = useState(0);
  const [pickedDayIso, setPickedDayIso] = useState<string | null>(null);

  const universalLink = useMemo(
    () => Object.values(selfBookLinks).find((l) => l.universal && l.artistId === artistId),
    [selfBookLinks, artistId],
  );

  // The artist's one universal, reusable self-book link. Minted on demand (in an
  // effect, never during render). Re-mints if it's absent so it self-heals the
  // async loadFixtures() boot, which replaces selfBookLinks and would otherwise
  // wipe a link minted a beat earlier.
  useEffect(() => {
    if (artist && !universalLink) {
      getOrCreateUniversalLink({ artistId: artist.id, studioId: artist.studioId });
    }
  }, [artist, universalLink]);

  // Build the client-facing URL for a given link, including pinned params.
  const buildLinkUrl = (l: SelfBookLink | undefined): string => {
    if (!l) return '';
    const q = l.pinnedStartIso
      ? `?d=${l.pinnedStartIso.slice(0, 10)}&t=${new Date(l.pinnedStartIso).getHours()}:00&len=${l.sessionLength}`
      : '';
    return `book.tattoovault.com.au/b/${l.token}${q}`;
  };

  const linkUrl = buildLinkUrl(universalLink);

  const myDeposits = useMemo(() => {
    if (!universalLink) return [];
    const prefix = `${universalLink.depositReference}-`;
    return Object.values(deposits).filter((d) => d.depositReference?.startsWith(prefix));
  }, [deposits, universalLink]);

  const copyLink = (url: string) => {
    if (!url) return;
    void navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const holdSlot = (
    session: 'small' | 'half-day' | 'full-day',
    custom?: { depositAud: number; durationMin: number },
  ) => {
    if (!universalLink || !chooserStartIso) return;
    // pinSelfBookSlot returns the freshly-pinned link; copy the URL built from THAT
    // (the render-time linkUrl closure is still the pre-pin value this tick).
    const { link: pinned } = pinSelfBookSlot({
      link: universalLink,
      session,
      startIso: chooserStartIso,
      ...(custom ? { custom } : {}),
    });
    setChooserStartIso(null);
    copyLink(buildLinkUrl(pinned));
  };

  const today = useMemo(() => new Date(clockIso), [clockIso]);
  const tomorrow = useMemo(() => addDays(today, 1), [today]);
  const weekEnd = useMemo(() => addDays(today, 7), [today]);

  // The month on show, and the day whose breakdown sits under it. Today's
  // breakdown is open by default; paging to a month without today shows the
  // grid alone until a day is picked.
  const month = useMemo(
    () =>
      artist
        ? buildArtistMonth({
            artist,
            bookings,
            monthIso: addMonths(new Date(clockIso), monthOffset).toISOString(),
            nowIso: clockIso,
          })
        : null,
    [artist, bookings, clockIso, monthOffset],
  );

  const defaultDayIso = month?.days.find((d) => d.isToday && d.kind === 'working')?.dayIso ?? null;
  const selectedDayIso = pickedDayIso ?? defaultDayIso;
  const selectedDay =
    month?.days.find((d) => d.dayIso === selectedDayIso && d.kind === 'working') ?? null;

  const artistBookings = useMemo(
    () =>
      Object.values(bookings)
        .filter((b) => b.artistId === artistId)
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
    [bookings, artistId],
  );

  // Payments the studio manager has sent to this artist's device, awaiting
  // hold-to-sign confirmation (D8 artist side), grouped into the runs they were
  // sent as: ONE payment can cover several work days, so it is confirmed once,
  // naming every day it settles. Subscribes to `payoutRecords` so a run clears the
  // instant `settlePayouts` writes the signed records.
  const runsToConfirm = useMemo(() => {
    if (!artist) return [];
    const sent = Object.values(payoutRecords)
      .filter((r) => r.artistId === artist.id && r.status === 'sent')
      .sort((a, b) => a.dayIso.localeCompare(b.dayIso));
    const byRun = new Map<string, typeof sent>();
    for (const r of sent) {
      const key = r.paymentRunId ?? r.id;
      byRun.set(key, [...(byRun.get(key) ?? []), r]);
    }
    return [...byRun.entries()].map(([id, records]) => ({
      id,
      records,
      totalAud: records.reduce((sum, r) => sum + r.amountDueAud, 0),
      payDayIso: records[0]!.paidOnDayIso ?? records[0]!.dayIso,
    }));
  }, [artist, payoutRecords]);

  // The artist's own live results (never other artists' numbers, never targets).
  const resultsFeed = useMemo(
    () => (artist ? artistResultsFeed(artist.id, clockIso) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artist, clockIso, bookings],
  );

  if (!artist) {
    return <p className="px-10 py-12 text-cream-soft italic">Unknown artist.</p>;
  }

  const todayBookings = artistBookings.filter((b) => isSameDay(new Date(b.start), today));
  const tomorrowBookings = artistBookings.filter((b) => isSameDay(new Date(b.start), tomorrow));
  const weekBookings = artistBookings.filter((b) => {
    const start = new Date(b.start);
    return start >= today && start <= weekEnd;
  });

  return (
    <section className="px-8 py-8" data-testid={`artist-dashboard-${artist.id}`}>
      <header className="flex items-end justify-between max-w-6xl">
        <div>
          <p className="label-small-caps text-gold-bright">Artist</p>
          <h1 className="heading-display text-3xl text-cream mt-2">{artist.displayName}</h1>
          <p className="font-inter text-xs text-cream-soft mt-2 uppercase tracking-widest">
            {artist.instagramHandle} · {artist.styleSpeciality.join(', ')} · {artist.studioId.replace('-', ' ')}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <DashboardKPITile
            label="Today"
            value={todayBookings.length.toString()}
            accent
            info="Sessions in your chair today."
          />
          <DashboardKPITile
            label="Tomorrow"
            value={tomorrowBookings.length.toString()}
            info="Sessions booked for tomorrow."
          />
          <DashboardKPITile
            label="This week"
            value={weekBookings.length.toString()}
            info="Sessions booked across the next seven days."
          />
        </div>
      </header>

      <nav className="mt-6 flex gap-2 flex-wrap border-b border-gold-dim/20 pb-2 max-w-6xl">
        {(
          [
            ['today', 'Today'],
            ['tomorrow', 'Tomorrow'],
            ['week', 'Week'],
            ['month', 'Month'],
            ['self-book', 'Self-book link'],
            ['earnings', 'Earnings'],
          ] as Array<[Tab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            data-testid={`artist-tab-${id}`}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border transition',
              tab === id
                ? 'border-gold-bright text-gold-bright bg-gold/10'
                : 'border-transparent text-cream-soft hover:text-cream hover:border-gold-dim/40',
            )}
          >
            {label}
            {id === 'earnings' && runsToConfirm.length > 0 ? (
              <span
                data-testid="earnings-tab-count"
                className="ml-1.5 inline-flex items-center justify-center rounded-full bg-gold-bright text-ink-black text-[9px] font-semibold h-4 min-w-[1rem] px-1"
              >
                {runsToConfirm.length}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="mt-6 max-w-6xl">
        {tab === 'today' ? (
          <SessionList
            bookings={todayBookings}
            customers={customers}
            onOpen={(b) => navigate(`/in-chair-rebooking/${b.id}`)}
            onFinish={(b) => navigate(`/in-chair-rebooking/${b.id}?finish=1`)}
            emptyMessage="No sessions today. The Stripe deposit ripple lands Sarah's Saturday slot, not today."
          />
        ) : null}
        {tab === 'tomorrow' ? (
          <SessionList
            bookings={tomorrowBookings}
            customers={customers}
            onOpen={(b) => navigate(`/in-chair-rebooking/${b.id}`)}
            emptyMessage="No sessions scheduled for tomorrow."
          />
        ) : null}
        {tab === 'week' ? (
          <ArtistWeekSchedule
            artist={artist}
            bookings={artistBookings}
            customers={customers}
            onOpen={(b) => navigate(`/in-chair-rebooking/${b.id}`)}
          />
        ) : null}
        {tab === 'month' && month ? (
          <div className="max-w-5xl" data-testid="artist-month-tab">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h3 className="heading-serif text-base text-cream">
                  {monthOffset === 0 ? 'This month' : format(new Date(month.monthStartIso), 'MMMM yyyy')}
                </h3>
                <p className="mt-1 font-inter text-[11px] uppercase tracking-widest text-cream-faint">
                  Number in the cell = hours open · strip under it = where those hours sit in the day
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid="month-prev"
                  onClick={() => setMonthOffset((o) => o - 1)}
                  aria-label="Previous month"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold-dim/40 text-cream-soft transition hover:border-gold-bright hover:text-gold-bright"
                >
                  <ChevronLeft size={15} />
                </button>
                <span
                  data-testid="month-range"
                  className="min-w-[8.5rem] px-2 text-center font-mono text-sm text-cream tabular-nums"
                >
                  {format(new Date(month.monthStartIso), 'MMMM yyyy')}
                </span>
                <button
                  type="button"
                  data-testid="month-next"
                  onClick={() => setMonthOffset((o) => o + 1)}
                  aria-label="Next month"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold-dim/40 text-cream-soft transition hover:border-gold-bright hover:text-gold-bright"
                >
                  <ChevronRight size={15} />
                </button>
                {monthOffset !== 0 ? (
                  <button
                    type="button"
                    data-testid="month-this"
                    onClick={() => setMonthOffset(0)}
                    className="ml-1 rounded-full border border-gold-dim/40 px-3 py-1.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft transition hover:text-gold-bright"
                  >
                    This month
                  </button>
                ) : null}
              </div>
            </div>

            <ArtistMonthGrid
              month={month}
              selectedDayIso={selectedDay?.dayIso ?? null}
              onSelect={setPickedDayIso}
            />

            {selectedDay ? (
              <div className="mt-5">
                <ArtistDaySchedule artistId={artist.id} dayIso={selectedDay.dayIso} />
              </div>
            ) : (
              <p className="mt-5 font-serif text-sm text-cream-faint" data-testid="month-no-day">
                Pick a working day to see how it breaks down.
              </p>
            )}
          </div>
        ) : null}
        {tab === 'self-book' ? (
          <div className="panel p-6 max-w-xl" data-testid="artist-self-book-panel">
            <h3 className="heading-serif text-base text-cream mb-1">Your self-book link</h3>
            <p className="font-serif text-cream-soft text-sm mb-4">
              Tap an open time to hold it. Your link pins to that day, time and length, and copies itself.
            </p>

            <ArtistSelfBookWeek
              artist={artist}
              heldStartIso={universalLink?.pinnedStartIso ?? null}
              onHold={(iso) => setChooserStartIso(iso)}
            />

            {universalLink?.pinnedStartIso ? (
              <p
                className="mt-4 rounded-md border border-gold-bright/50 bg-gold/10 px-3 py-2 font-serif text-xs text-cream"
                data-testid="held-slot-summary"
              >
                Holding {simTimeFull(universalLink.pinnedStartIso)} · {universalLink.sessionLength}. Held for the
                client for {holdExpiryHours(universalLink.pinnedStartIso, clockIso)}h, then it expires to a
                findable record.
              </p>
            ) : null}

            <p className="label-small-caps mt-5 mb-2">Your link</p>
            <div className="flex items-stretch rounded-md border border-gold-dim/40 overflow-hidden">
              <span className="flex-1 font-mono text-sm text-cream px-3 py-3 bg-ink-black truncate">
                {linkUrl}
              </span>
              <button
                type="button"
                data-testid="copy-self-book-link"
                onClick={() => copyLink(linkUrl)}
                className="btn-primary rounded-none px-5 text-xs flex items-center gap-1.5"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="font-mono text-[10px] text-cream-faint mt-2 break-all" data-testid="self-book-raw-link">
              {linkUrl}
            </p>

            {universalLink ? (
              <p className="font-mono text-xs text-cream-soft mt-4">
                Deposit reference{' '}
                <span className="text-gold-bright tracking-widest">{universalLink.depositReference}</span>{' '}
                · a unique number is stamped on every held slot.
              </p>
            ) : null}
            <button
              type="button"
              data-testid="preview-self-book-page"
              onClick={() => universalLink && navigate(`/customer-book/${universalLink.token}`)}
              className="btn-ghost text-xs mt-4"
            >
              Preview customer page
            </button>

            <p className="label-small-caps mt-6 mb-2">Sent links</p>
            <SelfBookStatusList deposits={myDeposits} customers={customers} />

            {chooserStartIso ? (
              <HoldLengthChooser
                artistName={artist.displayName}
                startIso={chooserStartIso}
                onPick={holdSlot}
                onClose={() => setChooserStartIso(null)}
              />
            ) : null}
          </div>
        ) : null}
        {tab === 'earnings' ? (
          <div className="space-y-4 max-w-3xl">
            {runsToConfirm.length > 0 ? (
              <div
                className="panel p-4 border border-gold-bright/40 bg-gold-bright/5"
                data-testid="payouts-to-confirm"
              >
                <p className="label-small-caps text-gold-bright">
                  Payment{runsToConfirm.length === 1 ? '' : 's'} to confirm
                </p>
                <p className="font-serif text-cream-soft text-xs mt-1">
                  Your studio manager has handed the cash over. Hold to confirm you&rsquo;ve received it.
                </p>
                <div className="mt-3 space-y-3">
                  {runsToConfirm.map((run) => (
                    <div
                      key={run.id}
                      className="panel-dark p-3 border border-gold-dim/25"
                      data-testid={`payout-confirm-row-${artist.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="heading-serif text-lg text-cream">{aud(run.totalAud)}</p>
                          <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-0.5">
                            {artist.studioId.replace('-', ' ')} ·{' '}
                            {run.records.length === 1 ? 'one day' : `${run.records.length} days`}
                          </p>
                        </div>
                      </div>
                      {/* Every work day this one payment settles, so a bundle is
                          never a mystery total: each day is listed, paid whole. */}
                      <table className="w-full text-sm mt-2.5">
                        <tbody>
                          {run.records.map((r) => (
                            <tr key={r.dayIso} data-testid={`payout-confirm-day-${r.dayIso}`}>
                              <td className="py-1 font-mono text-xs text-cream-soft">
                                {format(new Date(`${r.dayIso}T12:00:00+10:00`), 'EEE d MMM')}
                                {r.dayIso !== run.payDayIso ? (
                                  <span className="text-cream-faint italic ml-2">carried</span>
                                ) : null}
                              </td>
                              <td className="py-1 text-right font-mono text-xs text-cream">
                                {aud(r.amountDueAud)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-3">
                        <HoldToSign
                          testid={`payout-confirm-hold-${artist.id}`}
                          label="Hold to confirm receipt"
                          holdingLabel="Keep holding to confirm…"
                          onSigned={() => {
                            // The artist only ever confirms money actually handed
                            // over. Whether a short day was covered or deferred is
                            // the manager's decision, made before it was sent. One
                            // hold signs for every day in the payment.
                            settlePayouts({
                              studioId: artist.studioId,
                              artistId: artist.id,
                              dayIsos: run.records.map((r) => r.dayIso),
                              payDayIso: run.payDayIso,
                              signedByName: artist.displayName,
                              // The manager already decided this money was payable
                              // when they sent it; the artist's hold records receipt,
                              // it does not re-litigate the drawer.
                              allowShortDrawer: true,
                            });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-3 gap-4" data-testid="artist-results-tiles">
              <DashboardKPITile
                label="Appointments this week"
                value={(resultsFeed?.appointmentsThisWeek ?? 0).toString()}
                accent
                info="Your booked sessions over the last seven days. These are your numbers only — no one else's."
              />
              <DashboardKPITile
                label="Rebooks this week"
                value={(resultsFeed?.rebooksThisWeek ?? 0).toString()}
                info="Clients you rebooked in the chair this week — the next session locked in before they left. Tap ‘See clients’ below to view who."
              />
              <DashboardKPITile
                label="Rebook streak"
                value={(resultsFeed?.rebookStreak ?? 0).toString()}
                info="How many of your most recent finished sessions rebooked in a row. It resets the first time a session does not rebook."
              />
            </div>
            {resultsFeed && resultsFeed.rebookClients.length > 0 ? (
              <div className="panel-dark p-3" data-testid="artist-rebook-clients">
                <button
                  type="button"
                  data-testid="artist-rebook-clients-toggle"
                  onClick={() => setShowRebookClients((v) => !v)}
                  className="text-xs uppercase tracking-widest text-gold-bright hover:text-cream transition"
                >
                  {showRebookClients ? 'Hide clients' : `See clients (${resultsFeed.rebookClients.length})`}
                </button>
                {showRebookClients ? (
                  <ul className="mt-3 space-y-1">
                    {resultsFeed.rebookClients.map((c) => (
                      <li key={c.bookingId} className="font-inter text-sm text-cream-soft" data-testid={`artist-rebook-client-${c.bookingId}`}>
                        {c.name}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <ArtistEarningsDay artist={artist} dayIso={clockIso} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function SessionList({
  bookings,
  customers,
  onOpen,
  onFinish,
  emptyMessage,
}: {
  bookings: Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  onOpen: (b: ReturnType<typeof useBrainStore.getState>['bookings'][string]) => void;
  onFinish?: (b: ReturnType<typeof useBrainStore.getState>['bookings'][string]) => void;
  emptyMessage: string;
}) {
  if (bookings.length === 0) {
    return <p className="font-serif text-cream-soft">{emptyMessage}</p>;
  }
  return (
    <ol className="space-y-3">
      {bookings.map((b) => {
        const cust = customers[b.customerId];
        return (
          <li key={b.id} className="panel p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-inter text-lg text-cream">
                  {cust
                    ? customerName(cust)
                    : b.customerId === AWAITING_CLIENT
                      ? 'Awaiting client'
                      : b.customerId}
                </p>
                <p className="font-inter text-xs text-cream-soft mt-1 uppercase tracking-widest">
                  {simTimeFull(b.start)} · {Math.round(b.durationMin / 60)}h · {b.sessionLength}
                </p>
                <p className="font-serif text-sm text-cream-soft mt-3">{b.designBrief}</p>
                {b.referencePhotos.length > 0 ? (
                  <div className="flex gap-2 mt-3">
                    {b.referencePhotos.slice(0, 3).map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt="reference"
                        loading="lazy"
                        className="h-14 w-14 object-cover rounded border border-gold-dim/30"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-2">
                {b.status === 'confirmed' ? (
                  <span className="pill-mint text-[10px]">
                    <Wallet size={10} /> Deposit confirmed
                  </span>
                ) : (
                  <span className="pill-cream text-[10px]">{b.status}</span>
                )}
                <button
                  type="button"
                  onClick={() => onOpen(b)}
                  className="btn-ghost text-xs"
                >
                  <Sparkles size={10} /> Open record
                </button>
                {onFinish ? (
                  <button
                    type="button"
                    onClick={() => onFinish(b)}
                    className="btn-primary text-xs"
                    data-testid={`finish-session-${b.id}`}
                  >
                    <CheckCircle2 size={10} /> Finish session
                  </button>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

